import React from 'react';

import type { CardProps } from '@mui/material';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { forEach, isNil } from 'lodash';
import { createScopedI18n } from '../../../../i18n/i18n';
import { formatDate, formatTime, labelFieldEnum } from '../../../../utils/libs';
import AttendancesGroup from './attendances_group';

import type { JobHiringAttendance } from '../type';

const jobAttendanceCardI18n = createScopedI18n('pages.attendances');

export type JobAttendanceCardProps = {
  attendances: JobHiringAttendance[];
} & CardProps;

const attendanceClassify = (attendance: JobHiringAttendance): 'normal' | 'differTime' | 'overTime' => {
  const job = attendance.jobHiring?.job;
  const jobStartAt = job?.jobStartAt ? dayjs(job.jobStartAt) : null;
  const jobEndAt = job?.jobEndAt ? dayjs(job.jobEndAt) : null;
  const jobBreakDurationMinute = job?.breakDurationMinute;
  const jobOtMinute = isNil(job) || isNil(job.otHour) ? null : job.otHour * 60;

  const startOperationTime = attendance.startOperationTime ? dayjs(attendance.startOperationTime) : null;
  const endOperationTime = attendance.endOperationTime ? dayjs(attendance.endOperationTime) : null;
  const attendanceBreakDurationMinute = attendance.breakDurationMinute;

  let jobDurationMinute: number | null = job?.jobDurationMinute ?? null;
  if (isNil(jobBreakDurationMinute) && !isNil(jobStartAt) && !isNil(jobEndAt)) {
    jobDurationMinute = jobEndAt.diff(jobStartAt, 'minute') - (jobBreakDurationMinute ?? 0);
  }

  let attendanceDurationMinute: number | null = null;
  if (!isNil(startOperationTime) && !isNil(endOperationTime)) {
    attendanceDurationMinute =
      endOperationTime.diff(startOperationTime, 'minute') - (attendanceBreakDurationMinute ?? 0);
  }

  if (!isNil(jobOtMinute) && !isNil(attendanceDurationMinute) && attendanceDurationMinute > jobOtMinute) {
    return 'overTime';
  }

  if (!isNil(jobDurationMinute) && !isNil(attendanceDurationMinute) && attendanceDurationMinute > jobDurationMinute) {
    return 'overTime';
  }

  if (!isNil(jobStartAt) && !isNil(startOperationTime) && !jobStartAt.isSame(startOperationTime)) {
    return 'differTime';
  }

  if (!isNil(jobEndAt) && !isNil(endOperationTime) && !jobEndAt.isSame(endOperationTime)) {
    return 'differTime';
  }

  if (
    !isNil(jobBreakDurationMinute) &&
    !isNil(attendanceBreakDurationMinute) &&
    jobBreakDurationMinute !== attendanceBreakDurationMinute
  ) {
    return 'differTime';
  }

  return 'normal';
};

const JobAttendanceCard = ({ attendances, ...cardProps }: JobAttendanceCardProps) => {
  const job = React.useMemo(() => attendances[0]?.jobHiring?.job, [attendances]);

  const [normalAttendances, differTimeAttendances, overTimeAttendances] = React.useMemo(() => {
    const normal: JobHiringAttendance[] = [];
    const differTime: JobHiringAttendance[] = [];
    const overTime: JobHiringAttendance[] = [];

    forEach(attendances, (attendance) => {
      const classify = attendanceClassify(attendance);
      if (classify === 'normal') {
        normal.push(attendance);
      } else if (classify === 'differTime') {
        differTime.push(attendance);
      } else if (classify === 'overTime') {
        overTime.push(attendance);
      }
    });

    return [normal, differTime, overTime];
  }, [attendances]);

  return (
    <Card variant="outlined" {...cardProps}>
      <CardContent>
        {job ? (
          <Typography variant="h6">
            {labelFieldEnum('job.job_types', job.jobType)} {formatDate(job.jobStartAt, 'ddd DD MMM')}{' '}
            {formatTime(job.jobStartAt)} - {formatTime(job.jobEndAt)}
          </Typography>
        ) : null}

        <Stack paddingTop={1} gap={1}>
          <AttendancesGroup title={jobAttendanceCardI18n('normal_attendance')} attendances={normalAttendances} />
          <AttendancesGroup
            title={jobAttendanceCardI18n('different_time_attendance')}
            attendances={differTimeAttendances}
          />
          <AttendancesGroup title={jobAttendanceCardI18n('overtime_attendance')} attendances={overTimeAttendances} />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default JobAttendanceCard;
